import { useQuery } from "@blitzjs/rpc"
import { DestructedLocale } from "app/paywalls/helpers/types"
import { ProductSchema } from "integrations/revenueCat/product"
import { useMemo } from "react"
import { z } from "zod"
import getManualLocalizedProduct from "../queries/getManualLocalizedProduct"
export { FORM_ERROR } from "app/core/components/form/Form"

// Jake's hack for converting new products to old.
export function transformToOldProduct(deprecatedProductData: z.infer<typeof ProductSchema>) {
  const rawPeriod = deprecatedProductData.product_variables.subscription.period.default.full
  const period = rawPeriod === "months" ? "month" : rawPeriod
  const output = {
    currencyCode: deprecatedProductData.product_variables.currencyCode,
    currencySymbol: deprecatedProductData.product_variables.currencySymbol,
    dailyPrice: deprecatedProductData.product_variables.subscription.price.daily.raw.formatted,
    languageCode: deprecatedProductData.product_variables.languageCode,
    locale: deprecatedProductData.product_variables.locale,
    monthlyPrice: deprecatedProductData.product_variables.subscription.price.monthly.raw.formatted,
    period,
    periodAlt: `${deprecatedProductData.product_variables.subscription.period.default.duration.raw.formatted} ${deprecatedProductData.product_variables.subscription.period.default.short}`,
    periodly: `${period}ly`,
    periodDays: `${Math.floor(
      parseFloat(
        deprecatedProductData.product_variables.subscription.period.daily.duration.raw.value
      )
    )}`,
    periodMonths: `${Math.floor(
      parseFloat(
        deprecatedProductData.product_variables.subscription.period.monthly.duration.raw.value
      )
    )}`,
    periodWeeks: `${Math.floor(
      parseFloat(
        deprecatedProductData.product_variables.subscription.period.weekly.duration.raw.value
      )
    )}`,
    periodYears: `${Math.floor(
      parseFloat(
        deprecatedProductData.product_variables.subscription.period.yearly.duration.raw.value
      )
    )}`,
    price: deprecatedProductData.product_variables.subscription.price.default.raw.formatted,
    rawPrice: `${deprecatedProductData.product_variables.subscription.price.default.raw.value}`,
    trialPeriodDays: "",
    trialPeriodMonths: "",
    trialPeriodText: "",
    trialPeriodWeeks: "",
    trialPeriodYears: "",
    weeklyPrice: deprecatedProductData.product_variables.subscription.price.weekly.raw.formatted,
    yearlyPrice: deprecatedProductData.product_variables.subscription.price.yearly.raw.formatted,
  }

  if (
    deprecatedProductData.product_variables.trial &&
    deprecatedProductData.product_variables.trial.exists
  ) {
    output.trialPeriodDays = `${Math.floor(
      parseFloat(deprecatedProductData.product_variables.trial.period.daily.duration.raw.value)
    )}`
    output.trialPeriodMonths = `${Math.floor(
      parseFloat(deprecatedProductData.product_variables.trial.period.monthly.duration.raw.value)
    )}`
    output.trialPeriodWeeks = `${Math.floor(
      parseFloat(deprecatedProductData.product_variables.trial.period.weekly.duration.raw.value)
    )}`
    output.trialPeriodYears = `${Math.floor(
      parseFloat(deprecatedProductData.product_variables.trial.period.yearly.duration.raw.value)
    )}`
    output.trialPeriodText = deprecatedProductData.product_variables.trial.period.daily.full
      .replace(" ", "-")
      .replace("s", "")
  }

  return output
}

export function transformProductLocaleDataForPreview(
  productIdentifier: string | null,
  manualLocalizedProduct?: {}
) {
  if (productIdentifier === "" || !!!productIdentifier) {
    return {}
  } else {
    return manualLocalizedProduct || FallbackProductExample
  }
}

const FallbackProductExample = {
  currencyCode: "USD",
  currencySymbol: "$",
  dailyPrice: "$0.25",
  languageCode: "en",
  locale: "en_US@currency=USD",
  monthlyPrice: "$7.50",
  period: "year",
  periodAlt: "1 yr",
  periodDays: "365",
  periodly: "yearly",
  periodMonths: "12",
  periodWeeks: "52",
  periodYears: "1",
  price: "",
  rawPrice: "",
  trialPeriodDays: "7",
  trialPeriodMonths: "0",
  trialPeriodText: "7-day",
  trialPeriodWeeks: "1",
  trialPeriodYears: "0",
  weeklyPrice: "$1.73",
  yearlyPrice: "$89.99",
}

const useProductPreviewData = (
  applicationId: number | null,
  identifier: string | null,
  locale: DestructedLocale | null
) => {
  const { language, region, currency } = useMemo(() => {
    if (locale) {
      const { language, region, currency } = locale
      return { language, region, currency }
    } else return { language: null, region: null, currency: null }
  }, [locale])

  const [manualLocalizedProduct] = useQuery(
    getManualLocalizedProduct,
    {
      // These should never fire because of the enable clause
      applicationId: applicationId || 1,
      identifier: identifier || "",
      language: language || "",
      region: region || "",
      currency: currency || "",
    },
    {
      enabled:
        !!identifier &&
        identifier !== "" &&
        !!language &&
        !!region &&
        !!currency &&
        !!applicationId,
      suspense: false,
    }
  )

  const productVariables = useMemo(
    () => transformProductLocaleDataForPreview(identifier, manualLocalizedProduct),
    [identifier, manualLocalizedProduct]
  )

  return productVariables
}

export default useProductPreviewData
